import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Clock,
  Users,
  Calendar,
  Tag,
  Search,
  LayoutGrid,
  List,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function PackagesModule({ businessId, subscription }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [deletePackageId, setDeletePackageId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" veya "list"
  
  // Check if user can add/edit (has active paid subscription)
  const canEdit = subscription?.is_active && subscription?.plan !== 'free';

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    session_count: "",
    validity_days: "",
    branch: "",
    branches: [],  // Multi-branch (combo paket)
    session_duration: "55",
    session_type: "",
  });
  
  // Loading state for preventing double-click
  const [isSaving, setIsSaving] = useState(false);
  
  // Training types from API
  const [trainingTypes, setTrainingTypes] = useState([]);

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId]);

  const fetchData = async () => {
    try {
      const [packagesRes, businessRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/packages`, {
          credentials: "include",
        }),
        // Get business details to get branches AND training types
        fetch(`${API}/businesses/${businessId}`, {
          credentials: "include",
        }),
      ]);

      if (packagesRes.ok) setPackages(await packagesRes.json());
      
      // Get branches AND training types from business
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        
        // Branches
        const businessBranches = businessData.branches || [];
        const normalizedBranches = businessBranches.map(b => {
          if (typeof b === 'string') return { id: b, name: b, is_active: true };
          return { id: b.id || b.name || b, name: b.name || b, is_active: true };
        });
        setBranches(normalizedBranches);
        
        // Training types - use ONLY what business has configured, no defaults
        const businessTypes = businessData.training_types || [];
        // Normalize to objects with id and label
        const normalizedTypes = businessTypes.map(t => {
          if (typeof t === 'string') return { id: t, name: t, label: t };
          return { id: t.id || t.name, name: t.name || t.id, label: t.label || t.name };
        });
        setTrainingTypes(normalizedTypes);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const branchesList = (formData.branches && formData.branches.length > 0)
      ? formData.branches
      : (formData.branch ? [formData.branch] : []);
    if (!formData.name || !formData.session_count || !formData.validity_days || branchesList.length === 0) {
      toast.error(t("packages:toast_tumZorunluAlanlariDoldurunAd"));
      return;
    }

    // Prevent double-click
    if (isSaving) return;
    setIsSaving(true);

    try {
      const payload = {
        business_id: businessId,
        name: formData.name,
        session_count: parseInt(formData.session_count),
        validity_days: parseInt(formData.validity_days),
        branches: branchesList,
        session_duration: parseInt(formData.session_duration) || 55,
        session_type: formData.session_type || "private",
      };

      let response;
      if (editingPackage) {
        // PUT request for update
        response = await fetch(`${API}/packages/${editingPackage.package_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
      } else {
        // POST request for create
        response = await fetch(`${API}/packages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        toast.success(editingPackage ? t("packages:packageUpdated") : t("packages:packageCreated"));
        fetchData();
        handleCloseModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    const allBranches = (pkg.branches && pkg.branches.length > 0)
      ? pkg.branches
      : (pkg.branch ? [pkg.branch] : []);
    setFormData({
      name: pkg.name || "",
      session_count: (pkg.session_count ?? "").toString(),
      validity_days: (pkg.validity_days ?? "").toString(),
      branch: allBranches[0] || "",
      branches: allBranches,
      session_duration: (pkg.session_duration ?? 55).toString(),
      session_type: pkg.session_type || "private",
    });
    setShowModal(true);
  };

  const handleDelete = async (packageId) => {
    console.log("handleDelete called with:", packageId);
    setDeletePackageId(packageId);
  };
  
  const confirmDelete = async () => {
    if (!deletePackageId) return;
    if (!deleteReason.trim()) { toast.error("Silme nedeni zorunludur"); return; }
    
    try {
      console.log("Sending DELETE request for package:", deletePackageId);
      const response = await fetch(`${API}/packages/${deletePackageId}?reason=${encodeURIComponent(deleteReason.trim())}`, {
        method: "DELETE",
        credentials: "include",
      });
      console.log("Response status:", response.status);
      
      // Clone response to safely read body
      const responseClone = response.clone();
      let data;
      try {
        data = await responseClone.json();
        console.log("Response data:", data);
      } catch (parseErr) {
        console.error("JSON parse error:", parseErr);
        data = { detail: "Paket silinemedi" };
      }

      if (response.ok) {
        toast.success("Paket silindi", { duration: 3000 });
        fetchData();
      } else {
        toast.error(data.detail || "Paket silinemedi", { duration: 5000 });
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(t('members:deleteFailed'), { duration: 5000 });
    } finally {
      setDeletePackageId(null);
      setDeleteReason("");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPackage(null);
    setFormData({
      name: "",
      session_count: "",
      validity_days: "",
      branch: "",
      branches: [],
      session_duration: "55",
      session_type: "private",
    });
  };

  // Filtrelenmiş paketler
  const filteredPackages = packages.filter((pkg) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      pkg.name?.toLowerCase().includes(query) ||
      pkg.branches?.some(b => b.toLowerCase().includes(query)) ||
      pkg.branch?.toLowerCase().includes(query) ||
      pkg.session_type?.toLowerCase().includes(query)
    );
  }).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr", { numeric: true, sensitivity: "base" }));

  // Training type label'ı bul (API'den gelen listeye göre)
  const getTrainingTypeLabel = (type) => {
    if (!type) return "-";
    const found = trainingTypes.find(t => 
      t.id === type || t.name === type || t.label === type
    );
    return found?.label || found?.name || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('packages:packages')}</h2>
          <p className="text-grey text-sm">{packages.filter(p => p.is_active).length} {t('packages:activePackagesCount')}</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Görünüm Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className={`rounded-md px-2 ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`rounded-md px-2 ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>

          {canEdit && (
            <Dialog open={showModal} onOpenChange={(open) => {
              if (!open) {
                handleCloseModal();
              }
              setShowModal(open);
            }}>
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="add-package-btn"
                onClick={() => {
                  setEditingPackage(null);
                  setFormData({
                    name: "",
                    session_count: "",
                    validity_days: "",
                    branch: "",
                    branches: [],
                    session_duration: "55",
                    session_type: "private",
                  });
                  setShowModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('packages:newPackage')}
              </Button>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingPackage ? t('payments:editPackage') : t('payments:createNewPackage')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t("packages:packageName")}</Label>
                  <Input
                    placeholder={t("packages:ph_orn12SeansPilates")}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-xl"
                    data-testid="package-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("packages:sessionCount")}</Label>
                    <Input
                      type="number"
                      placeholder="12"
                      value={formData.session_count}
                    onChange={(e) => setFormData({ ...formData, session_count: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>{t("common:validityDays")} *</Label>
                  <Input
                    type="number"
                    placeholder="30"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({ ...formData, validity_days: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Label>{t("common:branch2")} *</Label>
                  {formData.branches.length >= 2 && (
                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">🔗 Combo Paket</Badge>
                  )}
                </div>
                <p className="text-xs text-grey mb-2">Birden fazla branş seçerseniz combo paket olur. Rezervasyon yaparken üye hangi branşta ders yapacağını seçebilir.</p>
                <div className="flex flex-wrap gap-2" data-testid="package-branches-multi">
                  {branches.map((branch) => {
                    const checked = formData.branches.includes(branch.name);
                    return (
                      <button
                        type="button"
                        key={branch.id}
                        onClick={() => {
                          const next = checked
                            ? formData.branches.filter((b) => b !== branch.name)
                            : [...formData.branches, branch.name];
                          setFormData({ ...formData, branches: next, branch: next[0] || "" });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                          checked
                            ? "bg-navy text-white border-navy"
                            : "bg-white text-navy border-slate-300 hover:border-navy"
                        }`}
                        data-testid={`package-branch-chip-${branch.name}`}
                      >
                        {checked && <span className="mr-1">✓</span>}
                        {getLocalizedBranch(branch.name)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("packages:sessionDuration")} *</Label>
                  <Input
                    type="number"
                    placeholder="55"
                    value={formData.session_duration}
                    onChange={(e) => setFormData({ ...formData, session_duration: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>{t("common:trainingType")} *</Label>
                  <Select 
                    value={formData.session_type} 
                    onValueChange={(value) => setFormData({ ...formData, session_type: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_antrenmanTipiSecin_76e4")} />
                    </SelectTrigger>
                    <SelectContent>
                      {trainingTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {getLocalizedTrainingType(type.label || type.name)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={handleCloseModal}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={handleSave}
                  disabled={isSaving}
                  data-testid="save-package-btn"
                >
                  {isSaving ? "Kaydediliyor..." : (editingPackage ? t('settings:update') : t('settings:create'))}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          )}
        </div>
        
        {!canEdit && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            {t("packages:noActiveSubscription")}
          </div>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
            <Input
              placeholder={t("packages:ph_paketAdiBransVeyaDers")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="search-packages"
            />
          </div>
        </CardContent>
      </Card>

      {/* Packages Grid/List */}
      {filteredPackages.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPackages.map((pkg) => (
              <Card
                key={pkg.package_id}
                className="border-0 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`package-card-${pkg.package_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading font-bold text-lg text-navy flex items-center gap-2">
                        {pkg.name}
                        {(pkg.branches?.length || 0) >= 2 && (
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px] px-1.5 py-0">🔗 Combo</Badge>
                        )}
                      </h3>
                      {(pkg.branches?.length > 0 || pkg.branch) && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(pkg.branches?.length > 0 ? pkg.branches : [pkg.branch]).map((b, i) => (
                            <Badge key={`${b}-${i}`} variant="secondary" className="text-xs">
                              {getLocalizedBranch(b)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleEdit(pkg)}
                      >
                        <Edit className="w-4 h-4 text-grey" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDelete(pkg.package_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-grey">
                        <Users className="w-4 h-4" />
                        {t("packages:sessionCountLabel")}
                      </span>
                      <span className="font-semibold text-navy">{pkg.session_count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-grey">
                        <Clock className="w-4 h-4" />
                        {t("packages:sessionDurationLabel")}
                      </span>
                      <span className="font-semibold text-navy">{pkg.session_duration || 55 } {t("common:minutes")}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-grey">
                        <Tag className="w-4 h-4" />
                        {t("common:trainingType")}
                      </span>
                      <span className="font-semibold text-navy">
                        {getTrainingTypeLabel(pkg.session_type)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-grey">
                        <Calendar className="w-4 h-4" />
                        {t("common:validityLabel")}
                      </span>
                      <span className="font-semibold text-navy">{pkg.validity_days} {t("common:days")}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <p className="text-xs text-grey italic">
                      {t("common:pricesManagedFromPriceList")}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* List View */
          <Card className="border-0 shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:branch")}</TableHead>
                    <TableHead className="whitespace-nowrap">Seans</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:duration")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:trainingType")}</TableHead>
                    <TableHead className="whitespace-nowrap">{t("common:validityDays")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPackages.map((pkg) => (
                    <TableRow key={pkg.package_id} data-testid={`package-row-${pkg.package_id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-navy" />
                          </div>
                          <span className="font-medium">{pkg.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-wrap gap-1 items-center">
                          {(pkg.branches?.length > 0 ? pkg.branches : (pkg.branch ? [pkg.branch] : [])).map((b, i) => (
                            <Badge key={`${b}-${i}`} variant="secondary" className="text-xs">
                              {getLocalizedBranch(b)}
                            </Badge>
                          )) || "-"}
                          {(pkg.branches?.length || 0) >= 2 && (
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px] px-1.5 py-0">🔗</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="font-semibold">{pkg.session_count}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span>{pkg.session_duration || 55 } {t("common:minutes")}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span>{getTrainingTypeLabel(pkg.session_type)}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span>{pkg.validity_days} {t("common:days")}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleEdit(pkg)}
                          >
                            <Edit className="w-4 h-4 text-grey" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => handleDelete(pkg.package_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-12 h-12 text-grey mx-auto mb-3" />
            <p className="text-grey">
              {searchQuery ? t("packages:packageNotFound") : t("packages:noPackageCreated")}
            </p>
            {canEdit && !searchQuery && (
              <Button
                className="mt-4 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={() => setShowModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("packages:createFirstPackage")}
              </Button>
            )}
            {!canEdit && !searchQuery && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                {t("packages:subscriptionRequiredForPackage")}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePackageId} onOpenChange={(open) => !open && setDeletePackageId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Paketi Sil</DialogTitle>
            <DialogDescription>
              {t("packages:deletePackageConfirm")}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Silme nedeni (zorunlu)"
            className="rounded-xl"
            data-testid="package-delete-reason-input"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeletePackageId(null)}
              className="rounded-xl"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="rounded-xl"
              disabled={!deleteReason.trim()}
              data-testid="package-delete-confirm-btn"
            >
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
